/**
 * Determine if a variable is empty.
 */
export function isEmpty(test) {
  return test === '' || 
         test === null || 
         test === undefined
}

/**
 * Format a number with USD currency.
 * 
 * @param  {Number}  val
 * @param  {Number}  decimals
 * @return {String}
 */
export function formatCurrency(val, decimals) {
  if (isNaN(val)) {
    val = 0
  }

  let currency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals, // (0 suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: decimals, // (0 causes 2500.99 to be printed as $2,501)
  }).format(val)

  // Trim trailing zeros if showing more than the standard US 2 decimals.
  // @note Update, by request of Tom on 5/29/2025 we want to show trailing zeros.
  // @see https://app.asana.com/1/1201422776129006/project/1204230877169171/task/1210416091008400?focus=true
  // if (decimals > 2) {
  //   let chars = currency.split('')
  //   while (
  //     chars[ chars.length - 1 ] === '0' &&
  //     chars[ chars.length - 3 ] !== '.' // min 2 digits
  //   ) {
  //     chars = chars.slice(0, -1)
  //   }
  //   return chars.join('')
  // }

  return currency
}

/**
 * Format a number.
 * 
 * @param  {Number}  val
 * @param  {Number}  decimals
 * @paarm  {Boolean}  commas
 * @return {String|Number}
 */
export function formatNumber(val, decimals, commas = true) {
  if (val === undefined || val === null || val === '') {
    return ''
  }

  val = val.toString().replace(',', '')

  if (isNaN(val)) {
    val = 0
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals, // (0 suffices for whole numbers, but will print 2500.10 as 2,500.1)
    maximumFractionDigits: decimals, // (0 causes 2500.99 to be printed as 2,501)
    useGrouping: commas,
  }).format(val)
}

/**
 * Format a date as ISO 8601 (YYYY-MM-DD).
 * 
 * @param  {Date}  date
 * @return {String}
 */
export function isoDateFormat(date) {
  return date.getFullYear() 
    + '-' 
    + ('0' + (date.getMonth() + 1)).slice(-2)
    + '-' 
    + ('0' + date.getDate()).slice(-2)
}

/**
 * Capitalize a string.
 * 
 * @param  {String}  val
 * @return {String}
 */
export function capitalize(val) {
  val = val.toString().toLowerCase().split(' ')

  return val.map(function(item) {
    return item.charAt(0).toUpperCase() + item.slice(1)
  }).join(' ')
}

/**
 * Get the offset of an element relative to the document.
 * 
 * @param  {Object}  el
 * @return {Object}
 */
export function offset(el) {
  const rect = el.getBoundingClientRect(), 
        bodyEl = document.body

  return {
    top: rect.top + bodyEl.scrollTop,
    left: rect.left + bodyEl.scrollLeft,
  }
}

/**
 * Collect and format an error message from an ajax response.
 * @see https://github.com/axios/axios#handling-errors
 */
export function getErrorMessageFromResponse(error) {
  if (
    error.response && 
    error.response.data && 
    error.response.data.errors
  ) {
    let msg = '<ul class="alerts error text-start">'
    for (const index in error.response.data.errors) {
      msg += '<li>' + error.response.data.errors[index] + '</li>'
    }
    msg += '</ul>'

    return msg
  }

  if (
    error.response && 
    error.response.data && 
    error.response.data.message
  ) {
    return '<p class="alerts error text-start">' + error.response.data.message + '</p>'
  }

  if (
    error.response && 
    error.response.data
  ) {
    let msg = '<ul class="alerts error text-start">'
    for (const index in error.response.data) {
      msg += '<li>' + error.response.data[index] + '</li>'
    }
    msg += '</ul>'

    return msg
  }

  return '<p class="alerts error text-start">An unexpected error occurred.</p>'
}
